import { useRef, useState, DragEvent } from 'react';
import { CircleXIcon, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';

/** 5 MB. Large enough for a logo or a scanned licence, small enough to upload. */
const DEFAULT_MAX_SIZE_BYTES = 5 * 1024 * 1024;

type Props = {
  file: File | null;
  setFile: (file: File | null) => void;
  onUpdate?: () => void;
  title: string;
  description: string;
  accept?: string;
  /** Overrides the 5 MB default. */
  maxSizeBytes?: number;
  /** Validation message from the parent form, shown under the drop zone. */
  error?: string;
};

const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${Math.round(bytes / (1024 * 1024))} MB`
    : `${Math.round(bytes / 1024)} KB`;

/**
 * Checks the file against `accept`, which the browser only enforces for the
 * picker dialog and not for drag-and-drop. Supports the `image/*` wildcard form
 * as well as explicit MIME types and extensions.
 */
const matchesAccept = (file: File, accept: string): boolean => {
  const patterns = accept
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean);
  if (patterns.length === 0) return true;

  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();

  return patterns.some((pattern) => {
    if (pattern.startsWith('.')) return name.endsWith(pattern);
    if (pattern.endsWith('/*')) return type.startsWith(pattern.slice(0, -1));
    return type === pattern;
  });
};

const FileUpload = ({
  file,
  setFile,
  onUpdate,
  title,
  description,
  accept = 'image/*',
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  error,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const { t } = useTranslation();

  /**
   * Neither size nor type was checked before, in either the picker or the drop
   * path, despite the copy promising limits. A file too large to upload used to
   * fail silently at submit.
   */
  const handleFile = (next: File | null) => {
    if (!next) {
      setLocalError(null);
      setFile(null);
      return;
    }

    if (!matchesAccept(next, accept)) {
      setLocalError(
        t('fileUpload.unsupportedType', {
          defaultValue: 'That file type is not supported.',
        })
      );
      return;
    }

    if (next.size > maxSizeBytes) {
      setLocalError(
        t('fileUpload.tooLarge', {
          size: formatBytes(maxSizeBytes),
          defaultValue: `File is too large. Maximum size is ${formatBytes(maxSizeBytes)}.`,
        })
      );
      return;
    }

    setLocalError(null);
    setFile(next);
    onUpdate?.();
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const message = localError ?? error;

  return (
    <div className="space-y-2">
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      className={cn(
        'relative cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors bg-background-main h-[176px] w-full flex flex-col items-center justify-center',
        isDragging
          ? 'border-primary bg-primary/5'
          : file
            ? 'border-green-300 bg-green-50'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50'
      )}
    >
      {file ? (
        <>
          <p className="mt-2 text-sm truncate text-muted-foreground">
            {file.name}
          </p>

          <button
            className="absolute top-2 right-2"
            onClick={(e) => {
              e.stopPropagation();
              setFile(null);
            }}
          >
            <CircleXIcon size={24} />
          </button>
        </>
      ) : (
        <>
          <Upload className="w-8 h-8 mx-auto mb-2" />

          <p className="text-[1rem] font-semibold leading-[100%]">{title}</p>
          <p className="mt-1 text-[1rem] leading-[100%] text-primary-black">
            {description}
          </p>
          <Button
            type="button"
            className="mt-3 rounded bg-secondary hover:bg-secondary-800"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            {t('campaignBuilder.creative.chooseFile')}
          </Button>
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0] ?? null);
          // Reset so re-picking the same file after an error still fires change.
          e.target.value = '';
        }}
      />
    </div>
    {message && <p className="text-sm text-destructive">{message}</p>}
    </div>
  );
};

export default FileUpload;
